import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getAuthRedirectPaths } from '../../utils/authRedirect';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SEO from '../../components/SEO';
import razorpayService from '../../utils/razorpayService';
import currencyService from '../../utils/currencyService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import FAQ from '../../components/ui/FAQ';
import { getFAQsForPage } from '../../utils/faqContent';
import { generatePageFAQSchema } from '../../utils/faqSchema';

import { supabase } from '../../utils/supabase';

const PricingPage = () => {
  const location = useLocation();
  const { registerPath } = getAuthRedirectPaths(location);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processingPlan, setProcessingPlan] = useState(null); // Track which plan is processing
  const [userCurrency, setUserCurrency] = useState('INR');
  const [userCountry, setUserCountry] = useState('IN');
  const [isLoading, setIsLoading] = useState(true);
  
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  
  // Get FAQs for pricing page
  const pricingFAQs = getFAQsForPage('pricing');
  const faqSchema = generatePageFAQSchema('pricing', 'https://mailvalidator.octalchip.com/pricing', {
    pageTitle: 'Pricing - Email Validator SaaS',
    pageDescription: 'Transparent pricing for email validation. Choose from monthly plans or usage-based pricing. Free plan available with 100 credits.'
  });
  
  // Usage-based pricing states
  const [usageBillingType, setUsageBillingType] = useState('monthly');
  // Usage-based pricing steps and tiers (no free tier)
  const usageSteps = [2000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000];
  const pricingTiers = [
    { min: 2000, max: 4999, price: 0.01 },
    { min: 5000, max: 9999, price: 0.009 },
    { min: 10000, max: 24999, price: 0.008 },
    { min: 25000, max: 49999, price: 0.0076 },
    { min: 50000, max: 99999, price: 0.0075 },
    { min: 100000, max: 249999, price: 0.00425 },
    { min: 250000, max: 499999, price: 0.00376 },
    { min: 500000, max: 999999, price: 0.0036 },
    { min: 1000000, max: Infinity, price: 0.00275 },
  ];

  // Update state to use only allowed steps - start with 2000 emails (minimum)
  const [emailCount, setEmailCount] = useState(2000);
  
  // Ensure email count is always a valid step
  useEffect(() => {
    if (!usageSteps.includes(emailCount)) {
      const closest = usageSteps.reduce((prev, curr) => Math.abs(curr - emailCount) < Math.abs(prev - emailCount) ? curr : prev);
      setEmailCount(closest);
    }
  }, [emailCount]);

  // Get current tier based on email count
  const getCurrentTier = () => {
    return pricingTiers.find(tier => emailCount >= tier.min && emailCount <= tier.max);
  };

  // Calculate total cost for usage-based pricing (USD only)
  const calculateUsageTotal = () => {
    const tier = getCurrentTier();
    console.log('Current email count:', emailCount);
    console.log('Current tier:', tier);
    
    if (!tier || tier.price === 0) return { total: 0, currency: 'USD', symbol: '$', isFree: true };
    try {
      const totalUSD = emailCount * tier.price;
      console.log('Total calculation:', emailCount, '*', tier.price, '=', totalUSD);
      return {
        total: totalUSD,
        currency: 'USD',
        symbol: '$',
        isFree: false
      };
    } catch (error) {
      console.error('Error calculating usage total:', error);
      return { total: 0, currency: 'USD', symbol: '$', isFree: false };
    }
  };

  // Format price for usage-based pricing (USD only)
  const formatUsagePrice = (amount, currency, symbol) => {
    if (amount === 0) return 'FREE';
    try {
      if (!amount || isNaN(amount)) return `${symbol}0.00000 USD`;
      return `${symbol}${amount.toFixed(5)} USD`;
    } catch (error) {
      console.error('Error formatting price:', error);
      return `${symbol}0.00000 USD`;
    }
  };

  // Handle email count change with exact step selection
  const handleEmailCountChange = (value) => {
    // Ensure value is one of the exact steps
    if (usageSteps.includes(value)) {
      console.log('Valid step selected:', value);
      setEmailCount(value);
    } else {
      // Find closest step
      const closest = usageSteps.reduce((prev, curr) => Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
      console.log('Invalid value:', value, 'Snapped to closest step:', closest);
      setEmailCount(closest);
    }
  };

  // Detect user location and currency on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Initialize exchange rates first
        await currencyService.initializeRates();
        
        // Check if there's stored pricing state (from payment intent)
        const pricingState = currencyService.loadPricingState();
        console.log('Found pricing state:', pricingState);
        if (pricingState) {
          console.log('Restoring from stored state:', pricingState);
          if (pricingState.currency) {
            console.log('Setting currency from stored state:', pricingState.currency);
            setUserCurrency(pricingState.currency);
          }
          if (pricingState.billingCycle) {
            setBillingCycle(pricingState.billingCycle);
          }
          // Don't detect location if we have stored state
          setUserCountry('US'); // Default for stored state
        } else {
          // Only detect user location if no stored state
          console.log('No stored state, detecting location...');
          const locationData = await currencyService.detectUserLocation();
          console.log('Location detected:', locationData);
          setUserCurrency(locationData.currency);
          setUserCountry(locationData.country);
        }
        
        // Debug: Test pricing calculation
        currencyService.testPricing();
      } catch (error) {
        console.warn('Could not initialize data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize if user is not logged in AND there's no stored payment intent
    const paymentIntent = localStorage.getItem('paymentIntent');
    if (!user && !paymentIntent) {
      initializeData();
    } else {
      // If user is logged in OR there's a payment intent, set loading to false
      setIsLoading(false);
    }
  }, [user]);

  // Check for stored payment intent after login
  useEffect(() => {
    if (user) {
      const paymentIntent = localStorage.getItem('paymentIntent');
      
      if (paymentIntent) {
        try {
          const intent = JSON.parse(paymentIntent);
          
          // Load pricing state using the new centralized system
          const pricingState = currencyService.loadPricingState();
          
          if (pricingState) {
            console.log('Restoring pricing state:', pricingState);
            
            // Restore the billing cycle and currency from the stored state
            if (pricingState.billingCycle) {
              setBillingCycle(pricingState.billingCycle);
            }
            if (pricingState.currency) {
              console.log('Setting currency to:', pricingState.currency);
              setUserCurrency(pricingState.currency);
            }
            
            // Clear the stored data
            localStorage.removeItem('paymentIntent');
            currencyService.clearPricingState();
            
            // Trigger payment for the stored plan with the correct currency and billing cycle
            setTimeout(() => {
              const storedCurrency = pricingState.currency || 'USD';
              const storedBillingCycle = pricingState.billingCycle || 'monthly';
              console.log('Processing payment with stored currency:', storedCurrency);
              console.log('Processing payment with stored billing cycle:', storedBillingCycle);
              
              // Pass the stored currency and billing cycle directly to handlePlanSelection
              handlePlanSelection(intent.plan, storedCurrency, storedBillingCycle);
            }, 1000); // Small delay to ensure state is updated
          }
        } catch (error) {
          console.error('Failed to process stored payment intent:', error);
          localStorage.removeItem('paymentIntent');
          currencyService.clearPricingState();
        }
      }
    }
  }, [user]);

  // Get dynamic pricing based on user's currency
  const getPricingPlans = () => {
    console.log('Getting pricing plans with userCurrency:', userCurrency);
    const currencyInfo = currencyService.getCurrencyInfo(userCurrency);
    console.log('Currency info:', currencyInfo);
    const symbol = currencyInfo?.symbol || '₹';
    
    return [
      {
        name: 'Starter',
        price: 'Free',
        period: null,
        description: 'Perfect for individual users and small projects',
        features: [
          '100 email validations per month',
          'Basic validation results',
          'Email format checking',
          'Disposable email detection',
          'Community support'
        ],
        cta: 'Get Started',
        ctaVariant: 'outline',
        popular: false,
        action: () => window.location.href = '/auth/register'
      },
      {
        name: 'Pro',
        price: (() => {
          console.log('Calculating Pro plan pricing for:', billingCycle, userCurrency);
          try {
            const pricing = currencyService.getPricingWithGST('Pro', billingCycle, userCurrency);
            console.log('Pro pricing result:', pricing);
            return pricing;
          } catch (error) {
            console.error('Error calculating Pro pricing:', error);
            return 'Error';
          }
        })(),
        period: billingCycle === 'monthly' ? '/mo' : '/year',
        description: 'Ideal for growing businesses and developers',
        features: [
          billingCycle === 'yearly' ? '120,000 email validations per year' : '10,000 email validations per month',
          'Advanced validation results',
          'Real-time validation',
          'Bulk email validation',
          'API access',
          'Priority support',
          'Custom validation rules',
          'Export results to CSV/JSON'
        ],
        cta: 'Pay Now',
        ctaVariant: 'primary',
        popular: true,
        savings: billingCycle === 'yearly' ? 'Save 17%' : null,
        action: () => handlePlanSelection('Pro'),

        razorpayButtonId: 'pl_QzHYtISFKs9ibZ' // Your Pro plan button ID
      },
      {
        name: 'Enterprise',
        price: (() => {
          console.log('Calculating Enterprise plan pricing for:', billingCycle, userCurrency);
          try {
            const pricing = currencyService.getPricingWithGST('Enterprise', billingCycle, userCurrency);
            console.log('Enterprise pricing result:', pricing);
            return pricing;
          } catch (error) {
            console.error('Error calculating Enterprise pricing:', error);
            return 'Error';
          }
        })(),
        period: billingCycle === 'monthly' ? '/mo' : '/year',
        description: 'For large organizations with high-volume needs',
        features: [
          'Unlimited email validations',
          'Advanced analytics dashboard',
          'Custom integrations',
          'Dedicated account manager',
          '24/7 phone support',
          'SLA guarantees',
          'White-label solutions',
          'Advanced security features'
        ],
        cta: 'Pay Now',
        ctaVariant: 'outline',
        popular: false,
        action: () => handlePlanSelection('Enterprise')
      }
    ];
  };

  const pricingPlans = getPricingPlans();

  // Handle plan selection and payment
  const handlePlanSelection = async (planName, forcedCurrency = null, forcedBillingCycle = null) => {
    if (!user) {
      // Store the intended payment plan and redirect to login
      const paymentIntent = {
        plan: planName,
        billingCycle: billingCycle,
        currency: userCurrency
      };
      localStorage.setItem('paymentIntent', JSON.stringify(paymentIntent));
      
      // Save pricing state using the new centralized system
      currencyService.savePricingState(userCurrency, billingCycle, planName);
      
      showInfo('Please log in to continue with your payment');
      setTimeout(() => {
        window.location.href = '/auth/login?redirect=payment';
      }, 1500);
      return;
    }

    // Set processing state for this specific plan only
    setProcessingPlan(planName);
    
    try {
      const planDetails = pricingPlans.find(plan => plan.name === planName);
      // Use forced currency and billing cycle if provided, otherwise use current state
      const currencyToUse = forcedCurrency || userCurrency;
      const billingCycleToUse = forcedBillingCycle || billingCycle;
      console.log('Using currency for payment:', currencyToUse);
      console.log('Using billing cycle for payment:', billingCycleToUse);
      const pricing = currencyService.getPricingWithGST(planName, billingCycleToUse, currencyToUse);
      
      // Update plan details with pricing info (use final amount with GST for Indian customers)
      const planWithPricing = {
        ...planDetails,
        amount: pricing.finalAmount || pricing.amount,
        currency: pricing.currency
      };
      
      // Call Supabase function directly to create order
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      
      if (!accessToken) {
        throw new Error('User not authenticated');
      }

      console.log('Sending request to Supabase function with:', {
        plan: planName,
        billingCycle: billingCycleToUse,
        amount: planWithPricing.amount,
        currency: planWithPricing.currency,
        userCurrency: userCurrency,
        pricing: pricing,
        planWithPricing: planWithPricing,
        baseAmount: pricing.originalAmountUSD,
        billingCycleToUse: billingCycleToUse,
        currencyToUse: currencyToUse
      });

      const response = await fetch('https://paziwddtrxxreqewroyr.supabase.co/functions/v1/create-razorpay-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          type: 'payment_link',
          amount: planWithPricing.amount, // Amount is already in smallest unit
          currency: planWithPricing.currency,
          description: `Payment for ${planName} plan - ${billingCycleToUse}`,
          notes: {
            plan: planName,
            billing_cycle: billingCycleToUse,
            user_id: user.id
          },
          callback_url: 'https://mailvalidator.octalchip.com/payment-return',
          callback_method: 'get'
        }),
      });

      if (response.ok) {
        const orderData = await response.json();
        console.log('Order created successfully:', orderData);
        
        showSuccess('Payment initiated successfully! Redirecting to payment page...');
        
        // Redirect to Razorpay payment page
        if (orderData.razorpay_response?.short_url) {
          setTimeout(() => {
            window.location.href = orderData.razorpay_response.short_url;
          }, 1500);
        } else {
          throw new Error('No payment URL received');
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to create order:', response.status, errorData);
        
        // Show specific error messages based on response
        if (response.status === 401) {
          showError('Authentication failed. Please log in again.');
        } else if (response.status === 400) {
          showError('Invalid request. Please check your plan selection.');
        } else if (response.status === 500) {
          showError('Server error. Please try again later.');
        } else {
          showError(`Payment initiation failed: ${response.status}. Please try again.`);
        }
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      
      // Show specific error messages based on error type
      if (error.message.includes('User not authenticated')) {
        showError('Please log in to continue with payment');
      } else if (error.message.includes('fetch')) {
        showError('Network error. Please check your connection and try again.');
      } else {
        showError('Payment initiation failed. Please try again.');
      }
    } finally {
      // Clear processing state for this plan
      setProcessingPlan(null);
    }
  };

  // Handle currency change
  const handleCurrencyChange = (newCurrency) => {
    setUserCurrency(newCurrency);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Pricing Plans"
        description="Choose the perfect email validation plan for your business. Flexible pricing options with usage-based billing, bulk discounts, and enterprise solutions. Start with 100 free credits!"
        keywords="email validation pricing, email verification plans, bulk email validation pricing, email validation cost, pricing tiers, subscription plans, enterprise pricing"
        canonical="https://mailvalidator.octalchip.com/pricing"
        structuredData={faqSchema}
      />
      <Header />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Page Header */}
        <div id="pricing-header" className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-text-primary">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Choose the perfect plan for your email validation needs. Scale up or down as your business grows.
          </p>
        </div>

        {/* Currency and Billing Controls */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-12">
          {/* Currency Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-text-secondary">Currency:</span>
            <div className="flex bg-surface border border-border rounded-md p-1">
              <button
                onClick={() => handleCurrencyChange('USD')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  userCurrency === 'USD'
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:text-primary'
                }`}
                disabled={isLoading}
              >
                $ USD
              </button>
              <button
                onClick={() => handleCurrencyChange('INR')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  userCurrency === 'INR'
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:text-primary'
                }`}
                disabled={isLoading}
              >
                ₹ INR
              </button>
            </div>
            {!isLoading && (
              <span className="text-xs text-text-secondary">
                Detected: {userCountry}
                {userCurrency === 'INR' && (
                  <span className="ml-2 text-orange-600 font-medium">
                    • GST applicable
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Billing Toggle */}
          <div className="bg-surface border border-border rounded-lg p-1 flex shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:text-primary'
              }`}
            >
              MONTHLY
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:text-primary'
              }`}
            >
              YEARLY (SAVE 17%)
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div id="pricing-plans" className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-surface rounded-lg p-8 border shadow-md animate-pulse">
                <div className="text-center mb-8">
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-12 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            pricingPlans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative bg-surface rounded-lg p-8 border shadow-md ${
                plan.popular
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2 text-text-primary">{plan.name}</h3>
                <div className="mb-4">
                  {(() => {
                    console.log('Rendering price for plan:', plan.name, 'Price object:', plan.price);
                    return typeof plan.price === 'string' ? (
                      // Free plan
                      <span className="text-4xl font-bold text-text-primary">{plan.price}</span>
                    ) : (
                      // Paid plans with GST
                      <div>
                        <span className="text-4xl font-bold text-text-primary">
                          {plan.price.finalFormatted || plan.price.formatted}
                        </span>
                        {plan.period && (
                          <span className="text-text-secondary ml-1">{plan.period}</span>
                        )}
                        {plan.price.gst && (
                          <div className="text-sm text-text-secondary mt-1">
                            <span>Base: {plan.price.baseFormatted}</span>
                            <span className="mx-1">+</span>
                            <span>GST: {plan.price.gst.formatted}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {plan.savings && (
                  <div className="text-success text-sm font-medium mb-2">
                    {plan.savings}
                  </div>
                )}
                <p className="text-text-secondary">{plan.description}</p>
              </div>

              <div className="mb-8">
                <h4 className="font-semibold mb-4 text-text-primary">Includes:</h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Icon name="Check" size={16} color="#00d68f" className="mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center">
                <Button
                  variant={plan.ctaVariant}
                  size="lg"
                  fullWidth
                  disabled={processingPlan === plan.name}
                  onClick={plan.action}
                  animation={plan.popular ? 'glow' : index % 4 === 0 ? 'bounce' : index % 4 === 1 ? 'float' : index % 4 === 2 ? 'pulse' : 'zoom'}
                  className={plan.popular ? 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70' : ''}
                >
                  {processingPlan === plan.name ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner size="sm" color="white" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    plan.cta
                  )}
                </Button>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Trusted By Section */}
        <div id="trusted-by" className="mt-24 text-center">
          <h2 className="text-2xl font-semibold mb-8 text-text-secondary">TRUSTED BY ENGINEERS AT</h2>
          <div className="grid grid-cols-5 gap-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">SAMSUNG</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">DATADOG</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">RAZORPAY</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">.monday.com</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">RIPPLING</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">perplexity</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">ramp</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">shopify</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">US. FOODS</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-text-secondary font-semibold text-lg">mercado libre</span>
            </div>
          </div>
        </div>

        {/* Usage-Based Pricing Calculator */}
        <div id="usage-based-pricing" className="mt-24">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Clock" size={20} />
              Coming Soon
            </div>
            <h2 className="text-3xl font-bold mb-4 text-text-primary">Usage-Based Pricing</h2>
            <p className="text-xl text-text-secondary">Pay only for what you use. More competitive</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Left Column - Calculator */}
            <div className="bg-surface rounded-xl p-8 border shadow-lg">
              {/* Billing Type Toggle */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-100 border border-border rounded-lg p-1 flex shadow-sm">
                  <button
                    onClick={() => setUsageBillingType('payg')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                      usageBillingType === 'payg'
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:text-primary'
                    }`}
                  >
                    Pay-As-You-Go
                  </button>
                  <button
                    onClick={() => setUsageBillingType('monthly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                      usageBillingType === 'monthly'
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:text-primary'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Email Count Input */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">
                  How many emails do you have?
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    value={emailCount.toLocaleString()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      handleEmailCountChange(Number(value));
                    }}
                    className="w-full px-4 py-3 border border-border rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary text-center"
                  />
                  <datalist id="usage-steps">
                    {usageSteps.map(step => (
                      <option key={step} value={step} />
                    ))}
                  </datalist>
                  <div className="text-sm text-text-secondary mt-2 text-center mb-8">
                    Our minimum purchase size is 2,000 credits
                  </div>
                </div>
              </div>

              {/* Slider */}
              <div className="mb-8 flex justify-center flex-col items-center">
                <div className="mb-4 text-xl font-semibold text-primary text-center">
                  ${getCurrentTier().price.toFixed(3)} per credit
                </div>
                <div className="relative w-3/4">
                  <input
                    type="range"
                    min={0}
                    max={8}
                    step={1}
                    value={usageSteps.indexOf(emailCount)}
                    onChange={e => {
                      const stepIndex = Number(e.target.value);
                      const stepValue = usageSteps[stepIndex];
                      console.log('Slider step:', stepIndex + 1, '/9, value:', stepValue.toLocaleString());
                      setEmailCount(stepValue);
                    }}
                    style={{
                      background: `linear-gradient(to right, #00d68f 0%, #00d68f ${(usageSteps.indexOf(emailCount) / 8) * 100}%, #e5e7eb ${(usageSteps.indexOf(emailCount) / 8) * 100}%, #e5e7eb 100%)`
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer custom-slider"
                  />
                  {/* Price label hidden */}
                </div>
              </div>

              {/* Total Cost */}
              <div className="text-center mb-8">
                <div className="text-sm text-text-secondary mb-2">YOUR TOTAL</div>
                <div className="text-3xl font-bold text-text-primary mb-2">
                  {calculateUsageTotal().isFree ? 'FREE' : `$${calculateUsageTotal().total.toFixed(2)} USD`}
                  <span className="text-lg text-text-secondary ml-1">
                    {usageBillingType === 'monthly' ? '/mo' : ''}
                  </span>
                </div>
              </div>

              {/* Currency Info */}
              <div className="text-center mb-8">
                <p className="text-sm text-text-secondary mb-2">
                  All major currencies accepted – including EUR, GBP and INR
                </p>
                <button className="text-primary hover:underline text-sm">
                  See the full list of currencies
                </button>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  style={{ backgroundColor: '#00d68f', color: '#fff', border: 'none' }}
                >
                  Get Started Free
                </Button>
                <div className="text-sm text-text-secondary mt-4">
                  Get 100 free monthly credits - business or premium domain required
                </div>
              </div>
            </div>

            {/* Right Column - Pricing Tiers */}
            <div className="space-y-8">
              {/* Monthly Rates */}
              <div className="bg-surface rounded-xl p-8 border shadow-lg">
                <h3 className="text-2xl font-bold mb-2 text-text-primary">Pay-As-You-Go</h3>
                <p className="text-text-secondary mb-6">No contract. Cancel anytime.</p>
                
                <div className="space-y-4">
                  {pricingTiers.map((tier, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <div className="text-text-primary">from {tier.min.toLocaleString()}</div>
                      <div className="font-semibold text-primary">{formatUsagePrice(tier.price, 'USD', '$')}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enterprise */}
              <div className="bg-surface rounded-xl p-8 border shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-text-primary">Enterprise</h3>
                <p className="text-text-secondary mb-6">
                  over 1,000,000 emails? <a href="/contact" className="text-primary hover:underline">Contact us</a>
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => window.location.href = '/contact'}
                >
                  Request a demo
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="mt-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary">Frequently Asked Questions</h2>
            <p className="text-lg text-text-secondary">Everything you need to know about our pricing and plans</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <FAQ items={pricingFAQs} variant="default" />
          </div>
          
          {/* Contact Links */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/docs">
              <Button variant="outline" size="md">
                Our Documentation
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="md">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>



        {/* CTA Section - Only show for non-authenticated users */}
        {!user && (
          <div id="pricing-cta" className="mt-24 text-center">
            <h2 className="text-3xl font-bold mb-4 text-text-primary">Ready to get started?</h2>
            <p className="text-text-secondary mb-8">Join thousands of users who trust EmailValidator for their email validation needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={registerPath}>
                <Button variant="primary" size="lg">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/docs">
                <Button variant="outline" size="lg">
                  View API Documentation
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* GST Notice for Indian Customers */}
        {userCurrency === 'INR' && (
          <div className="mt-12 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-orange-800 mb-2">
                🧾 GST Information for Indian Customers
              </h3>
              <p className="text-orange-700 text-sm">
                All prices shown above include 18% GST for Indian customers. 
                GST is applicable on all paid plans (Pro & Enterprise) as per Indian tax regulations.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage; 